import { FC, useMemo } from "react";
import classNames from "classnames";
import {
    addMonths,
    addWeeks,
    formatDate,
    isBefore,
    startOfMonth,
    startOfWeek,
} from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { currencyFormatter } from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { useClientPricing } from "@hooks/billing/useClientPricing";
import { useConstant } from "@hooks/useConstant";
import { useBillableClients } from "../../useBillableClients";
import styles from "../BillingPage.module.scss";

export const MonthlyInvoiceSection: FC = () => {
    const clients = useBillableClients();
    const { organizationOwnedBillingPricing } = useClientPricing();

    const upcomingInvoiceDate = useConstant(() => {
        const firstDayOfNextMonth = startOfMonth(addMonths(new Date(), 1));
        const mondayOfTheWeek = startOfWeek(firstDayOfNextMonth, {
            weekStartsOn: 1,
        });

        if (isBefore(mondayOfTheWeek, firstDayOfNextMonth)) {
            return addWeeks(mondayOfTheWeek, 1);
        }

        return mondayOfTheWeek;
    });

    const totalCents = useMemo(
        () =>
            clients.reduce((acc, client) => {
                const plan = client.plan;
                const price = plan
                    ? (organizationOwnedBillingPricing[plan] ?? 0)
                    : 0;
                return acc + price;
            }, 0),
        [clients, organizationOwnedBillingPricing],
    );

    return (
        <article className={styles.section}>
            <header className={styles.header}>
                <h2 className={styles.heading}>Monthly invoice</h2>
            </header>

            <main className={styles.sectionContent}>
                <div className={styles.grid}>
                    <div className={styles.row}>
                        <div className={classNames(styles.column, styles.item)}>
                            Upcoming invoice date
                        </div>
                        <div
                            className={classNames(styles.column, styles.value)}
                        >
                            {upcomingInvoiceDate
                                ? formatDate(
                                      upcomingInvoiceDate,
                                      fullDateFullYearFormat,
                                  )
                                : "N/A"}
                        </div>
                    </div>
                    <div className={classNames(styles.row, styles.footer)}>
                        <div className={classNames(styles.column, styles.item)}>
                            Estimated total
                        </div>
                        <div
                            className={classNames(styles.column, styles.value)}
                        >
                            {currencyFormatter.format(
                                fromIntToMonetary(totalCents),
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </article>
    );
};
